<template>
  <div style="position: absolute; left: -9999px; top: -9999px">
    <div
      ref="pdfWrapper"
      class="pdf-container bg-white"
      style="
        width: 800px;
        padding: 50px;
        color: black;
        font-family: 'Helvetica', 'Arial', sans-serif;
        box-sizing: border-box;
        position: relative;
        margin: 0;
        line-height: 1.6;
      "
    >
      <!-- Header with Logo and Company Info -->
      <div
        style="
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
        "
      >
        <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 6px">
          <div v-if="orgLogoUrl" style="height: 60px; max-width: 180px; overflow: hidden">
            <img
              :src="orgLogoUrl"
              alt="Logo"
              style="height: 100%; width: auto; object-fit: contain"
              crossorigin="anonymous"
            />
          </div>
          <div>
            <div v-if="!orgLogoUrl" style="font-weight: bold; font-size: 16px">
              {{ orgData.name || 'Zavron Finance Pvt. Ltd.' }}
            </div>
            <div style="font-size: 12px; color: #555">
              {{ orgAddressLine1 }}, {{ orgAddressCity }}, {{ orgAddressState }}
            </div>
            <div style="font-size: 12px; color: #555">
              Landline: {{ orgData.phone || '0712-2520007' }}
            </div>
          </div>
        </div>
        <div style="text-align: right; font-size: 12px; color: #555">
          <div>Date: {{ formattedDate }}</div>
        </div>
      </div>

      <hr style="border: 1px solid #1e3a8a; margin-bottom: 2rem" />

      <!-- Title -->
      <h2
        style="
          text-align: center;
          font-size: 22px;
          font-weight: bold;
          margin-bottom: 2rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #1e3a8a;
        "
      >
        No Dues Certificate
      </h2>

      <!-- Body -->
      <div style="font-size: 14px; margin-bottom: 1.5rem; text-align: justify">
        <p style="margin-bottom: 1rem">
          We hereby confirm that the Loan Agreement no:
          <strong>{{ loanDetails.caseNo || '' }}</strong> of Rs
          <strong>{{ formatCurrencyPdf(totalLoanAmount) }}</strong> granted to
          <strong>{{ customerFirstName }} {{ customerMiddleName }} {{ customerLastName }}</strong>
          having PAN no: <strong>{{ customerPan || 'XXXXXXXXXX' }}</strong> and having registered
          address at <strong>{{ customerAddress }}</strong> has paid all Dues in respect of the said
          loan as on <strong>{{ formattedDate }}</strong
          >.
        </p>
        <p style="margin-bottom: 1rem">
          There are No Dues Pending against the said Loan Account number as cleared on
          <strong>{{ lastReceivedDate }}</strong
          >.
        </p>
        <p style="margin-bottom: 1rem">
          Furthermore, the agreement signed by you also stands terminated and any unbanked/unused
          cheques issued by you in favour of
          <strong>{{ orgData.name || 'Zavron Finance Pvt. Ltd.' }}</strong> with regards to the
          above-mentioned loan account shall be cancelled and/or destroyed immediately.
        </p>
        <p style="margin-bottom: 2rem">
          Thank you once again for selecting
          <strong>{{ orgData.name || 'Zavron Finance Pvt. Ltd.' }}</strong> as your preferred
          financial partner.
        </p>
      </div>

      <div style="margin-top: 3rem">
        <p style="font-size: 14px; margin-bottom: 0.5rem">Sincerely,</p>
        <p style="font-size: 14px; font-weight: bold; margin-top: 2.5rem">Authorised Signatory</p>
        <p style="font-size: 14px">For {{ orgData.name || 'Zavron Finance Pvt. Ltd.' }}</p>
      </div>

      <hr style="border: 1px solid #ccc; margin: 2rem 0" />

      <!-- Footer -->
      <div style="font-size: 11px; color: #666; text-align: center; font-style: italic">
        In case of any queries and concerns, please reach us at
        {{ orgData.email || 'info@zavronfinserv.com' }} or at our nearest branch.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NocLetterPdfTemplate',
  props: {
    orgData: { type: Object, default: () => ({}) },
    closureDate: { type: String, default: '' },
    customerFirstName: { type: String, default: '' },
    customerMiddleName: { type: String, default: '' },
    customerLastName: { type: String, default: '' },
    customerPan: { type: String, default: '' },
    customerAddress: { type: String, default: '' },
    customerCity: { type: String, default: '' },
    customerState: { type: String, default: '' },
    loanDetails: { type: Object, default: () => ({}) },
    totalLoanAmount: { type: Number, default: 0 },
    lastReceivedDate: { type: String, default: '' }
  },
  computed: {
    formattedDate() {
      if (!this.closureDate)
        return new Date()
          .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
          .replace(/\//g, '-')
      return new Date(this.closureDate)
        .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        .replace(/\//g, '-')
    },
    orgLogoUrl() {
      if (!this.orgData?.logo) return null
      let url = ''
      if (
        this.orgData.logo.startsWith('http://') ||
        this.orgData.logo.startsWith('https://') ||
        this.orgData.logo.startsWith('data:')
      ) {
        url = this.orgData.logo
      } else {
        url = `${process.env.VUE_APP_S3_BASE_URL || 'https://finfi-uat.s3.ap-south-1.amazonaws.com/'}${this.orgData.logo}`
      }
      const baseUrl = this.$axios?.defaults?.baseURL || 'https://uatapi.myfinfi.com'
      return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`
    },
    orgAddressLine1() {
      return this.orgData?.organization_address?.address_line_1 || ''
    },
    orgAddressCity() {
      return this.orgData?.organization_address?.city || 'Nagpur'
    },
    orgAddressState() {
      return this.orgData?.organization_address?.state || 'Maharashtra'
    }
  },
  methods: {
    formatCurrencyPdf(value) {
      const val = Number(value) || 0
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val)
    },
    getHtmlElement() {
      return this.$refs.pdfWrapper
    }
  }
}
</script>
